import { useTranslation } from "react-i18next";
import AccountHeader from "../AccountHeader";
import type { AccountHeaderProps } from "../AccountHeader";
import { Box, Button } from "@mui/material";
import { useParams } from "react-router";
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import useStoreData from "../../Common/_hooks/Store/useStoreData";
import { STORE_ICON_URLS } from "../StoreIconUrls";

interface Props extends Omit<AccountHeaderProps, 'titlePrefix'> {}

export default function StoreHeader(props: Props) {
  const [t] = useTranslation('account-v2')
  const params = useParams<{ storeSlug: string }>()
  const store = useStoreData(params.storeSlug)
  const storeIconUrl = params.storeSlug ? STORE_ICON_URLS[params.storeSlug] : undefined
  const storeName = store?.get('name') ?? ''
  const storeAdminUrl = store?.get('store_admin_url')
  const storeLabel = storeName || t('Store')
  const storeTitle = storeName ? t('{{store}} Store', { store: storeName }) : t('Store')
  const storeAdminLabel = storeName
    ? t('Open {{store}} admin', { store: storeName })
    : t('Open store admin')

  return <AccountHeader
    titlePrefix={<Button
      variant="text"
      size="small"
      color="inherit"
      disabled={!storeAdminUrl}
      component={storeAdminUrl ? 'a' : 'button'}
      href={storeAdminUrl}
      target={storeAdminUrl ? '_blank' : undefined}
      rel={storeAdminUrl ? 'noopener noreferrer' : undefined}
      aria-label={storeAdminUrl ? storeAdminLabel : storeTitle}
      title={storeAdminUrl ? storeAdminLabel : storeTitle}
      startIcon={<Box
        component="img"
        src={storeIconUrl}
        alt=""
        sx={{
          width: 20,
          height: 20,
          objectFit: 'contain',
        }}
      />}
      sx={{
        height: 36,
        minWidth: 36,
        px: 1,
        borderRadius: '999px',
        bgcolor: 'rgba(0, 0, 0, 0.06)',
        color: 'text.primary',
        flexShrink: 0,
        overflow: 'hidden',
        textTransform: 'none',
        transition: theme => theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&.Mui-disabled': {
          color: 'text.primary',
          bgcolor: 'grey.100',
          opacity: 1,
        },
        '& .MuiButton-startIcon': {
          m: 0,
        },
        '&:hover, &:focus-visible': {
          bgcolor: 'rgba(0, 0, 0, 0.12)',
        },
        '& .store-admin-button-label': {
          maxWidth: 0,
          ml: 0,
          opacity: 0,
          overflow: 'hidden',
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          fontSize: '1rem',
          fontWeight: 500,
          lineHeight: 'normal',
          transform: 'translateX(-4px)',
          transition: theme => theme.transitions.create(['margin-left', 'max-width', 'opacity', 'transform'], {
            duration: theme.transitions.duration.shorter,
          }),
        },
        '&:hover .store-admin-button-label, &:focus-visible .store-admin-button-label': {
          maxWidth: {
            xs: 'min(56vw, 220px)',
            sm: 320,
          },
          ml: 1,
          opacity: 1,
          transform: 'translateX(0)',
        },
      }}
    >
      <Box component="span" className="store-admin-button-label">
        {storeLabel}
        {storeAdminUrl ? <OpenInNewRoundedIcon
          sx={{
            ml: 0.75,
            fontSize: 14,
            color: 'text.secondary',
          }}
        /> : null}
      </Box>
    </Button>}
    title={props.title}
    titleActions={props.titleActions}
    breadcrumbs={props.breadcrumbs}
    actions={props.actions}
  />
}
